const setListenedTranscript = transcriptId => {
  return {
    type: 'SET_LISTENED_TRANSCRIPT',
    transcriptId,
  };
};

const _ = require('lodash');

const MAX_AUDIO_SAVED_MS = 20 * 60 * 1000;

export function getVolume(chunk) {
  let sum = 0;
  for (let i = 0; i < chunk.length; i += 1) {
    sum += chunk[i] ** 2;
  }
  return sum / chunk.length;
}

export const resample = async (buffer, sourceSampleRate, targetSampleRate) => {
  window.OfflineAudioContext =
    window.OfflineAudioContext || window.webkitOfflineAudioContext;
  const numFrames = (buffer.length * targetSampleRate) / sourceSampleRate;

  const offlineContext = new OfflineAudioContext(
    1,
    numFrames,
    targetSampleRate
  );
  const bufferSource = offlineContext.createBufferSource();
  bufferSource.buffer = offlineContext.createBuffer(
    1,
    buffer.length,
    sourceSampleRate
  );
  const b = bufferSource.buffer.getChannelData(0);
  for (let i = 0; i < buffer.length; i += 1) {
    b[i] = buffer[i];
  }

  return new Promise(resolve => {
    offlineContext.oncomplete = event => {
      resolve(event.renderedBuffer.getChannelData(0));
    };
    bufferSource.connect(offlineContext.destination);
    bufferSource.start(0);
    offlineContext.startRendering();
  });
};

function Float32ToInt16(inputArray) {
  const output = new Int16Array(inputArray.length);
  for (let i = 0; i < inputArray.length; i += 1) {
    output[i] = inputArray[i] * 0x8000;
  }
  return output;
}

function concatenate(ResultConstructor, ...arrays) {
  let totalLength = 0;
  arrays.forEach(arr => {
    totalLength += arr.length;
  });
  const result = new ResultConstructor(totalLength);
  let offset = 0;
  arrays.forEach(arr => {
    result.set(arr, offset);
    offset += arr.length;
  });
  return result;
}

export class AudioBuffer {
  constructor(
    sourceSampleRateHz,
    chunkLengthMs,
    sendAudioCallback,
    volumesCallback
  ) {
    this.sourceSampleRateHz = sourceSampleRateHz;
    this.chunkLengthMs = chunkLengthMs;
    this.sendAudioCallback = sendAudioCallback;
    this.volumesCallback = volumesCallback;
    this.buffer = new Float32Array(0);
    this.currentTs = Date.now();
    this.startTs = Date.now();
    this.lastPlaybackTimeMs = 0;
    this.state = 'recording';
  }

  get tsFromAudioMs() {
    return (
      this.startTs +
      Math.round(
        this.lastPlaybackTimeMs -
          (this.buffer.length * 1000) / this.sourceSampleRateHz
      )
    );
  }

  addChunk(e) {
    const float32Data = e.inputBuffer.getChannelData(0);
    this.lastPlaybackTimeMs = 1000 * e.playbackTime;

    this.buffer = concatenate(Float32Array, this.buffer, float32Data);
    this.sendAudioIfNeeded();
  }

  async sendAudioIfNeeded() {
    const reset =
      Math.abs(Date.now() - this.currentTs) > 10 * this.chunkLengthMs;
    if (reset) {
      this.currentTs = Date.now() - this.chunkLengthMs;
    }
    while (
      this.buffer.length >
      (this.chunkLengthMs * this.sourceSampleRateHz) / 1000
    ) {
      const d = this.buffer.slice(
        0,
        (this.chunkLengthMs / 1000) * this.sourceSampleRateHz
      );
      this.buffer = this.buffer.slice(
        (this.chunkLengthMs / 1000) * this.sourceSampleRateHz
      );
      this.currentTs += this.chunkLengthMs;
      this.convertAndSendAudio(d, this.currentTs);
    }
  }

  convertAndSendAudio(audio, ts) {
    const int16Data = Float32ToInt16(audio);
    const buffer = Buffer.allocUnsafe(int16Data.length * 2);
    int16Data.forEach((_d, index) =>
      buffer.writeInt16LE(this.state === 'recording' ? _d : 0, index * 2)
    );
    this.volumesCallback(getVolume(audio));
    this.sendAudioCallback({
      audio_chunk: buffer.toString('base64'),
      timestamp: ts,
      audio_timestamp: this.tsFromAudioMs,
    });
  }

  stopRecording() {
    this.state = 'stopped';
  }

  startRecording() {
    this.state = 'recording';
  }
}

export function convertDataURIToBinary(base64) {
  const raw = window.atob(base64);
  const rawLength = raw.length;
  const array = new Uint16Array(new ArrayBuffer(rawLength));

  for (let i = 0; i < rawLength; i += 1) {
    // eslint-disable-next-line no-bitwise
    const val = raw.charCodeAt(2 * i) | (raw.charCodeAt(2 * i + 1) << 8);
    // eslint-disable-next-line no-bitwise
    array[i] = val & 0x8000 ? val | 0xffff0000 : val;
  }
  return array;
}

export function int16ToFloat32(inputArray, startIndex, length) {
  const output = new Float32Array(inputArray.length - startIndex);
  for (let i = startIndex; i < length; i += 1) {
    const int = inputArray[i];
    // If the high bit is on, then it is a negative number, and actually counts backwards.
    // eslint-disable-next-line bitwise
    const float = int >= 0x8000 ? -(0x10000 - int) / 0x8000 : int / 0x7fff;
    output[i] = float;
  }
  return output;
}

export function decodeChunk(chunk) {
  if (!chunk.audio || chunk.decodedData) {
    return chunk;
  }
  const binaryArray = convertDataURIToBinary(chunk.audio);
  const floatArray = int16ToFloat32(binaryArray, 0, binaryArray.length);
  return {
    ...chunk,
    decodedData: floatArray,
    audio: undefined,
  };
}

const delayStepMs = 5000;

class AudioPlayer {
  constructor() {
    this.listeners = [];
    this.speed = 1;
    this.secondsBehindBuffer = [];
    this.paused = undefined;
    this.dynamicSpeed = true;
  }

  addReduxTool(getState, dispatch) {
    this.getState = getState;
    this.dispatch = dispatch;
  }

  register(component) {
    this.listeners.push(component);
    component.setState({
      delay: this.delay,
      speed: this.speed,
      state: this.audioCtx ? this.audioCtx.state : undefined,
      paused: this.paused,
    });
  }

  unregister(component) {
    this.listeners.splice(this.listeners.indexOf(component));
  }

  updateState(state) {
    this.listeners.forEach(l => l.setState(state));
  }

  jumpToDelay() {
    if (undefined === this.secondsBehind) return;
    this.replayAll(this.secondsBehind * 1000 - this.delay);
  }

  updateSpeed(speed) {
    if (speed === this.speed || !this.dynamicSpeed) {
      return;
    }
    this.speed = speed;
    this.updateState({ speed });
    this.replayAll();
  }

  setState(a) {
    this.listeners.forEach(l => l.setState(a));
  }

  playPause() {
    if (!this.paused) {
      this.id = Math.random();
      Array.from(Object.keys(this.scheduleChunks)).forEach(ts => {
        this.scheduleChunks[ts].stop();
        delete this.scheduleChunks[ts];
      });
      this.paused = this.lastEndedTimestamp;
      this.paused = Date.now();
    } else {
      this.delay = this.secondsBehind * 1000;
      this.replayAll(-100 * Math.round((Date.now() - this.paused) / 100) - 200);
      this.paused = undefined;
    }
    this.setState({ paused: this.paused, delay: this.delay });
  }

  decrementDelay() {
    if (this.paused) {
      this.playPause();
      return;
    }
    this.delay = this.secondsBehind * 1000 - delayStepMs;
    this.updateState({ delay: this.delay });
  }

  incrementDelay() {
    if (this.paused) {
      this.playPause();
      return;
    }
    this.delay = this.secondsBehind * 1000 + delayStepMs;
    this.updateState({ delay: this.delay });
  }

  decrementTinyDelay() {
    if (this.paused) {
      this.playpause();
      return;
    }
    this.delay = this.secondsBehind * 1000 - delayStepMs / 5;
    this.updateState({ delay: this.delay });
  }

  incrementTinyDelay() {
    if (this.paused) {
      this.playpause();
      return;
    }
    this.delay = this.secondsBehind * 1000 + delayStepMs / 5;
    this.updateState({ delay: this.delay });
  }

  increaseGain() {
    if (this.gainNode) {
      this.gainNode.gain.value *= 1.1;
      this.updateState({ gain: this.gainNode.gain.value });
    }
  }

  decreaseGain() {
    if (this.gainNode) {
      this.gainNode.gain.value /= 1.1;
      this.updateState({ gain: this.gainNode.gain.value });
    }
  }

  reset({ sampleRateHertz, chunkLengthMs, delay, shift }) {
    if (this.audioCtx) {
      this.audioCtx.close();
    }
    this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    this.gainNode = this.audioCtx.createGain();
    this.gainNode.connect(this.audioCtx.destination);
    this.audioCtx.onstatechange = ({ target: { state } }) => {
      this.listeners.forEach(l => l.setState({ state }));
    };
    this.lastEndedAudioCtxTime = 0;
    this.audioCtxStartTime = 0;
    this.speed = 1;
    this.secondsBehindBuffer = [];

    this.sampleRateHertz = sampleRateHertz || this.sampleRateHertz || 16000;
    this.chunkLengthMs = chunkLengthMs || this.chunkLengthMs || 100;
    this.delay = this.delay || delay;
    this.secondsBehind = undefined;
    this.lastEndedTimestamp = null;
    this.chunks = this.chunks || {};
    this.scheduleChunks = {};
    this.firstAudioTimestamp = null;
    this.lastScheduledChunkTimestamp = null;
    this.updateState({ delay: this.delay, speed: this.speed });
    this.replayAll(shift * 1000);
  }

  handleAudioMessage(event) {
    let message = JSON.parse(event.data);
    // hack to handle as well scribe and web
    if (!message.data) {
      message = {
        type: message.type,
        data: { ...message, timestamp: message.timestampMs },
      };
    }
    if (message.type === 'audio') {
      const { timestamp, audio } = message.data;
      if (this.audioCtx.state === 'suspended') {
        this.audioCtx.resume();
        this.listeners.forEach(l => l.setState({ state: this.audioCtx.state }));
      } else if (this.audioCtx && !this.firstAudioTimestamp) {
        this.firstAudioTimestamp = timestamp;
        this.replayAll(-this.delay - this.audioCtx.currentTime * 1000);
      }
      const chunk = decodeChunk({
        timestamp,
        audio,
        decodedData: null,
      });
      delete this.chunks[chunk.timestamp - MAX_AUDIO_SAVED_MS];
      delete this.scheduleChunks[chunk.timestamp - MAX_AUDIO_SAVED_MS];
      if (this.chunks[chunk.timestamp]) {
        // eslint-disable-next-line no-console
        console.log('ignore chunk', chunk.timestamp);
        return;
      }
      this.chunks[chunk.timestamp] = chunk;
      if (!this.paused) {
        const { delay } = this.playChunk(chunk);
        if (delay < 0) {
          this.handleSecondBehind(delay);
        }
      } else {
        this.handleSecondBehind(
          parseInt((chunk.timestamp - this.lastEndedTimestamp) / 100, 10) / 10
        );
      }
    }
  }

  destructor() {
    if (this.audioCtx) this.audioCtx.close();
  }

  replayAll(sh) {
    this.id = Math.random();
    if (this.lastEndedTimestamp) {
      this.audioCtxStartTime =
        this.lastEndedAudioCtxTime + this.chunkLengthMs / 1000;
      // eslint-disable-next-line
      this.firstAudioTimestamp =
        this.lastEndedTimestamp + (sh || 0) + this.chunkLengthMs;
    } else {
      this.audioCtxStartTime = this.audioCtxStartTime || 0;
      this.audioCtxStartTime -= (sh || 0) / 1000;
    }
    Array.from(Object.keys(this.scheduleChunks)).forEach(ts => {
      this.scheduleChunks[ts].stop();
      delete this.scheduleChunks[ts];
    });
    Array.from(Object.values(this.chunks)).forEach(chunk => {
      this.playChunk(chunk);
    });
    this.secondsBehindBuffer = [];
    this.secondsBehind = this.delay / 1000;
    this.lastEndedTimestamp = this.firstAudioTimestamp;
    this.lastEndedAudioCtxTime = this.audioCtxStartTime;
  }

  handleSecondBehind(secondsBehind) {
    // eslint-disable-next-line
    secondsBehind = parseInt(secondsBehind * 10, 10) / 10;
    this.updateState({
      secondsBehind,
    });
    if (this.secondsBehindBuffer.length > 20) {
      this.secondsBehindBuffer.shift();
    }
    this.secondsBehindBuffer.push(secondsBehind);
    this.secondsBehind = _.mean(this.secondsBehindBuffer);
    const lowSpeed = 0.9;
    const normalSpeed = 1;
    const highSpeed = 1.05;
    const sb = 1000 * this.secondsBehind;
    const margin = 1000;

    switch (this.speed) {
      case lowSpeed:
        if (sb > this.delay) {
          // this.updateSpeed(normalSpeed);
        }
        break;
      case normalSpeed:
        if (sb > this.delay + margin) {
          // this.updateSpeed(highSpeed);
        } else if (sb < this.delay - margin) {
          // this.updateSpeed(lowSpeed);
        }
        break;
      case highSpeed:
        if (sb < this.delay) {
          // this.updateSpeed(normalSpeed);
        }
        break;
      default:
        break;
    }
  }

  onEndedChunk = (chunk, scheduleTime, id) => {
    /*
     * Called after each played chunk
     */
    if (id !== this.id) {
      return;
    }
    delete this.scheduleChunks[chunk.timestamp];
    this.lastEndedTimestamp = chunk.timestamp;
    this.lastEndedAudioCtxTime = scheduleTime;
    const secondsBehind =
      parseInt((this.lastScheduledChunkTimestamp - chunk.timestamp) / 100, 10) /
      10;
    this.handleSecondBehind(secondsBehind);
    const timestamp = chunk.timestamp + 1000;
    if (timestamp / 1000 === Math.round(timestamp / 1000)) {
      const { transcripts } = window.store.getState().scribeConversation;
      const transcriptsId = Array.from(Object.keys(transcripts)).sort(
        (a, b) => a - b
      );
      let lastTranscriptId = transcriptsId[0];
      let found;
      // eslint-disable-next-line
      for (let transcriptId of transcriptsId) {
        if (parseFloat(transcriptId) > timestamp) {
          found = true;
          break;
        }
        lastTranscriptId = transcriptId;
      }
      if (!found) {
        lastTranscriptId = transcriptsId[transcriptsId.length - 1];
      }
      if (
        lastTranscriptId !==
        window.store.getState().scribeConversation.listenedTranscriptId
      )
        window.store.dispatch(setListenedTranscript(lastTranscriptId));
    }
  };

  playChunk(chunk) {
    if (!this.firstAudioTimestamp) {
      return {};
    }
    const scheduleTime =
      Math.round(
        this.audioCtxStartTime * 1000 +
          (chunk.timestamp - this.firstAudioTimestamp) / this.speed
      ) / 1000;
    if ((!scheduleTime && scheduleTime !== 0) || scheduleTime < 0) {
      return {};
    }
    if (scheduleTime < this.audioCtx.currentTime) {
      // this.handleSecondBehind(scheduleTime - this.audioCtx.currentTime);
      return { delay: scheduleTime - this.audioCtx.currentTime };
    }
    const src = this.audioCtx.createBufferSource();
    const { id } = this;
    src.onended = () => this.onEndedChunk(chunk, scheduleTime, id);
    const audioBuffer = this.audioCtx.createBuffer(
      1,
      (this.chunkLengthMs / 1000) * this.sampleRateHertz,
      this.sampleRateHertz
    );
    const nowBuffering = audioBuffer.getChannelData(0);
    for (let i = 0; i < audioBuffer.length; i += 1) {
      if (chunk.decodedData) {
        nowBuffering[i] = chunk.decodedData[i];
      }
    }
    src.buffer = audioBuffer;
    src.looping = false;
    src.playbackRate.value = this.speed;
    src.connect(this.gainNode);
    src.start(scheduleTime);
    // console.log('schecdule ', chunk.timestamp, scheduleTime, id);
    this.scheduleChunks[chunk.timestamp] = src;
    this.lastScheduledChunkTimestamp = chunk.timestamp;
    return { delay: scheduleTime - this.audioCtx.currentTime };
  }
}

// eslint-disable-next-line
export let audioPlayer = new AudioPlayer();
